// TotalMentionsCard: expanded version of the "Toplam Bahsedilme" tile.
// Core product-team ask: clicking any data point on the dashboard must reveal
// where it came from. Each source row (Trustpilot, Reddit, YouTube, etc.) is
// expandable into the underlying sample quotes — and every quote carries a
// direct URL back to the original review/post.

import React, { useState } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { ReviewSignal, DataSource, Provider } from '../lib/api';

interface TotalMentionsCardProps {
  totalMentions: number;
  signals: ReviewSignal[];
  dataSources: DataSource[];
  providers?: Provider[];
  lang: Lang;
}

const SOURCE_LABELS: Record<string, string> = {
  reddit: 'Reddit',
  trustpilot: 'Trustpilot',
  'app store': 'App Store',
  appstore: 'App Store',
  'play store': 'Play Store',
  playstore: 'Play Store',
  x: 'X / Twitter',
  twitter: 'X / Twitter',
  youtube: 'YouTube',
  blog: 'Blog',
  news: 'Haber',
  web: 'Web',
};

function friendlyName(s: string): string {
  return SOURCE_LABELS[s.toLowerCase()] ?? s;
}

interface SampleQuote {
  text: string;
  source: string;
  url?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  date?: string;
}

// Each signal row's sample_quotes is a JSON-encoded array. Parse defensively —
// some rows have malformed JSON or empty strings.
function parseQuotes(raw: string | null | undefined): SampleQuote[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((q) => q && typeof q.text === 'string');
  } catch {
    return [];
  }
}

interface SourceDrilldownItem {
  text: string;
  url?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  date?: string;
  providerId: string;
}

// Collect all sample quotes for a given source across every provider.
function quotesForSource(source: string, signals: ReviewSignal[]): SourceDrilldownItem[] {
  const needle = source.toLowerCase();
  const out: SourceDrilldownItem[] = [];
  for (const s of signals) {
    if (s.source.toLowerCase() !== needle) continue;
    for (const q of parseQuotes(s.sample_quotes)) {
      out.push({
        text: q.text,
        url: q.url,
        sentiment: q.sentiment,
        date: q.date,
        providerId: s.provider_id,
      });
    }
  }
  return out;
}

function sentimentDot(s?: string): string {
  if (s === 'positive') return 'bg-emerald-500/70';
  if (s === 'negative') return 'bg-rose-500/70';
  if (s === 'neutral') return 'bg-neutral-500/60';
  return 'bg-neutral-300';
}

export function TotalMentionsCard({ totalMentions, signals, dataSources, providers = [], lang }: TotalMentionsCardProps) {
  const [openSource, setOpenSource] = useState<string | null>(null);

  // Aggregate per-source counts from signals (excluding zero-row stubs)
  const byCounts = new Map<string, number>();
  for (const s of signals) {
    if (s.mention_count <= 0) continue;
    const key = s.source.toLowerCase();
    byCounts.set(key, (byCounts.get(key) ?? 0) + s.mention_count);
  }

  // Also merge data_sources data if signals are empty (synthesizer-only summary)
  if (byCounts.size === 0 && dataSources.length > 0) {
    for (const ds of dataSources) {
      if (!ds.success || ds.records_fetched <= 0) continue;
      byCounts.set(ds.name.toLowerCase(), ds.records_fetched);
    }
  }

  const sortedSources = Array.from(byCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const providerName = (id: string) => providers.find((p) => p.id === id)?.name ?? id;

  return (
    <div className="card p-4 sm:col-span-2 lg:col-span-1">
      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">
        {t('totalMentions', lang)}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-neutral-900">
        {totalMentions}
      </div>

      {sortedSources.length > 0 && (
        <div className="mt-3 space-y-1">
          {sortedSources.map(([source, count]) => {
            const pct = totalMentions > 0 ? Math.round((count / totalMentions) * 100) : 0;
            const quotes = quotesForSource(source, signals);
            const expandable = quotes.length > 0;
            const isOpen = openSource === source;
            return (
              <div key={source} className="text-xs">
                <button
                  type="button"
                  onClick={() => expandable && setOpenSource(isOpen ? null : source)}
                  disabled={!expandable}
                  className={`w-full flex items-center gap-2 text-left ${expandable ? 'hover:text-neutral-900 cursor-pointer' : 'cursor-default'}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`truncate flex items-center gap-1 ${expandable ? 'text-neutral-700' : 'text-neutral-500'}`}>
                        {friendlyName(source)}
                        {expandable && (
                          <span className="text-neutral-500 text-[10px]">{isOpen ? '▲' : '▼'}</span>
                        )}
                      </span>
                      <span className="text-neutral-500 tabular-nums shrink-0 ml-2">
                        {count} · {pct}%
                      </span>
                    </div>
                    <div className="h-0.5 bg-neutral-200 rounded-full overflow-hidden mt-0.5">
                      <div
                        className="h-full bg-neutral-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </button>
                {isOpen && quotes.length > 0 && (
                  <ul className="mt-1.5 ml-1 space-y-1.5 pl-2 border-l border-neutral-200">
                    {quotes.slice(0, 8).map((q, idx) => (
                      <li key={idx} className="text-[11px] leading-snug">
                        <div className="flex items-start gap-1.5">
                          <span className={`w-1 h-1 rounded-full ${sentimentDot(q.sentiment)} mt-1.5 shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <span className="text-neutral-700">“{q.text}”</span>
                            <div className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                              <span>{providerName(q.providerId)}</span>
                              {q.date && <span>· {q.date}</span>}
                              {q.url && q.url.startsWith('http') && (
                                <a
                                  href={q.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-emerald-600/80 hover:text-emerald-600 underline decoration-dotted"
                                >
                                  {lang === 'tr' ? 'kaynak →' : 'source →'}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                    {quotes.length > 8 && (
                      <li className="text-[10px] text-neutral-500">
                        +{quotes.length - 8} {lang === 'tr' ? 'daha' : 'more'}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 text-[10px] text-neutral-500 leading-tight">
        {lang === 'tr'
          ? 'Halka açık platformlardan toplanan, sağlayıcı bazlı kullanıcı geri bildirimleri. Bir kaynağa tıklayarak ham yorumları ve linklerini görebilirsiniz.'
          : 'User feedback collected from public platforms, per provider. Click a source to view raw quotes and their links.'}
      </div>
    </div>
  );
}
